import React from "react";

/**
 * A React component that arranges its children into a tri-column layout, with each child 
 * receiving a sequential animation delay based on its index.
 *
 * @param {Object} props - The props object.
 * @param {React.ReactNode} props.children - The child elements to be rendered within the tri-column layout.
 * @returns {JSX.Element} The rendered tri-column layout.
 */
const TriCards = ({ children }) => {
    return (
        <>
            <div className="duo-cards tri-mode">
                {React.Children.map(children, (child, index) => {
                    if (React.isValidElement(child)) {
                        return React.cloneElement(child, { aosDelay: index * 100 });
                    }
                    return child;
                })}
            </div>
        </>
    );
}

/**
 * A subcomponent of TriCards that represents an individual card within the layout.
 *
 * @param {Object} props - The props object.
 * @param {string | Object} [props.title] - The title of the card. Can be a string or a custom element.
 * @param {string} [props.title.custom] - The custom element for the title.
 * @param {string | Object} [props.description] - The description of the card. Can be a string or a custom element.
 * @param {string} [props.description.custom] - The custom element for the description.
 * @param {Object} [props.heading] - The heading of the card, containing optional text, image, or a custom element.
 * @param {string} [props.heading.text] - The text for the heading.
 * @param {string} [props.heading.image] - The source URL for the heading image.
 * @param {string} [props.heading.alt] - The alt text for the heading image.
 * @param {React.ReactNode} [props.heading.custom] - The custom element for the heading.
 * @param {number} [props.aosDelay=0] - The delay for the AOS animation (in milliseconds).
 * @returns {JSX.Element} The rendered card element.
 */
TriCards.Card = ({ title, description, heading, aosDelay }) => {
    return (
        <div className="duo-card" data-aos="fade-up" data-aos-delay={aosDelay}>
            {heading?.text && <span>{heading.text}</span>}
            {heading?.image && heading?.alt && <img src={heading.image} alt={heading.alt} />}
            {heading?.custom && heading?.custom}

            {typeof title == 'string' && <h1>{title}</h1>}
            {title?.custom && title?.custom}

            {typeof description == 'string' && <p>{description}</p>}
            {description?.custom && description?.custom}
        </div>
    )
}

export default TriCards;
